interface SkeletonProps {
  size?: 'sm' | 'md' | 'lg' | 'block';
  w?: number | string;
  h?: number | string;
  className?: string;
  style?: React.CSSProperties;
  circle?: boolean;
}

export function Skeleton({ size = 'md', w, h, className = '', style, circle }: SkeletonProps) {
  const cls = circle ? 'skeleton sk-circle' : `skeleton ${size === 'sm' ? 'sk-sm' : size === 'lg' ? 'sk-lg' : size === 'block' ? 'sk-block' : ''}`;
  return <span className={`${cls} ${className}`.trim()} style={{ width: w, height: h, ...style }} />;
}

/**
 * La fascia di numeri mentre carica.
 *
 * Ricalca `.stat-band`: numeri NUDI sul foglio, separati da un filo verticale,
 * margine 10. Prima disegnava `grid grid-N mb-6 border border-line`, cioè una
 * griglia KPI incorniciata — un disegno che nell'app non esiste più — e appena
 * arrivavano i dati diventava una fascia nuda con 14px di margine in meno.
 * Succedeva su dodici schermate di elenco.
 *
 * `withIcon` non ha più effetto sul disegno (la `.stat-band` vera non ha
 * icone): resta nella firma perché la passano già cinque pagine, e toglierla
 * vorrebbe dire toccarle tutte per niente.
 */
export function SkeletonKPIRow({ count = 4 }: { count?: number; withIcon?: boolean }) {
  return (
    <div className="stat-band">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i}>
          {/* Le stesse tre righe di una cella vera: etichetta, numero, nota. */}
          <div className="st-label"><Skeleton size="sm" w={110} /></div>
          <div className="st-value"><Skeleton size="lg" w={130} h={27} /></div>
          <div className="st-delta"><Skeleton size="sm" w={90} /></div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonTableRow({ cols, withAvatar = true }: { cols: number; withAvatar?: boolean }) {
  return (
    <tr>
      {withAvatar && <td><Skeleton circle w={28} h={28} /></td>}
      {Array.from({ length: cols - (withAvatar ? 1 : 0) }).map((_, i) => (
        <td key={i}>
          <Skeleton size="sm" w={i === 0 ? 200 : 90 + (i % 3) * 20} />
        </td>
      ))}
    </tr>
  );
}

export function SkeletonTable({ rows = 5, cols = 6, withAvatar = true }: { rows?: number; cols?: number; withAvatar?: boolean }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <SkeletonTableRow key={i} cols={cols} withAvatar={withAvatar} />
      ))}
    </>
  );
}

export function SkeletonCard({ titleW = 200, blocks = 2 }: { titleW?: number; blocks?: number }) {
  return (
    <div className="card">
      <div className="card-bd skeleton-col">
        <Skeleton size="lg" w={titleW} />
        {Array.from({ length: blocks }).map((_, i) => (
          <Skeleton key={i} size="block" />
        ))}
      </div>
    </div>
  );
}

/**
 * Skeleton parametrico per le detail page: ricalca la silhouette reale
 * (back-link + header + KPI row + cards) così non c'è layout-shift quando
 * arrivano i dati. Sostituisce il flash "Caricamento…".
 *
 * @param header  'avatar' (cerchio, es. cliente) | 'square' (box iniziali) |
 *                'chips' (page-header con chip di stato, es. fattura/preventivo)
 */
export function DetailSkeleton({
  header = 'chips',
  kpis = 4,
  cards = 2,
}: {
  header?: 'avatar' | 'square' | 'chips';
  kpis?: number;
  cards?: number;
}) {
  return (
    <div className="page">
      {/* Il back-link: stessa aria di `nav.breadcrumb`, che ha mb 14 — non 12. */}
      <div className="row" style={{ gap: 6, marginBottom: 14 }}>
        <Skeleton size="sm" w={14} h={14} style={{ borderRadius: 4 }} />
        <Skeleton size="sm" w={120} />
      </div>

      {/* L'intestazione vera delle schede è `.page-ed-hd` (pb 22 + mb 10) con
          `.ed-title` alto 45px, non `.page-header items-start` con un titolo da
          32: erano 13px di salto sul titolo e una struttura diversa, su tredici
          pagine di dettaglio. I bottoni sono alti 36 (--h-ctl), non 38. */}
      <div className="page-ed-hd flex items-end justify-between">
        <div className="flex items-end gap-3 min-w-0">
          {header === 'avatar' && <Skeleton circle w={56} h={56} />}
          {header === 'square' && <Skeleton w={56} h={56} style={{ borderRadius: 'var(--r-tile)' }} />}
          <div className="space-y-2 min-w-0">
            {header === 'chips' && (
              <div className="flex gap-2 mb-1">
                <Skeleton size="sm" w={70} style={{ borderRadius: 'var(--r-pill)' }} />
                <Skeleton size="sm" w={90} style={{ borderRadius: 'var(--r-pill)' }} />
              </div>
            )}
            {/* 50 e 20 non sono numeri a caso: misurata l'intestazione vera di
                /clients/:id, /suppliers/:id e /services/:id, il blocco di
                sinistra è alto 105,1 (chip + titolo 50,5 + riga meta) e il
                gruppo dei bottoni 40. Prima erano 32 e 38. */}
            <Skeleton size="lg" w={280} h={50} />
            <Skeleton size="sm" w={200} h={20} />
          </div>
        </div>
        <div className="flex gap-2">
          <Skeleton w={120} h={40} style={{ borderRadius: 'var(--r-ctl)' }} />
          <Skeleton w={100} h={40} style={{ borderRadius: 'var(--r-ctl)' }} />
        </div>
      </div>

      {kpis > 0 && <SkeletonKPIRow count={kpis} />}

      {/* Le due colonne di un dettaglio hanno gutter 24 (gap-6), non 20: il
          gap-5 era l'unico dell'applicazione. */}
      <div className="grid gap-6" style={{ gridTemplateColumns: '2fr 1fr' }}>
        <div className="col gap-6">
          {Array.from({ length: cards }).map((_, i) => <SkeletonCard key={i} titleW={160} blocks={i === 0 ? 2 : 1} />)}
        </div>
        <div className="col gap-6">
          <SkeletonCard titleW={120} blocks={1} />
          <SkeletonCard titleW={140} blocks={1} />
        </div>
      </div>
    </div>
  );
}
